import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { AxiosError } from "axios";
import { ContractPreviewModal, BessOnboardingContractData, ContractSigningPayload } from "../components/bessOnboarding/ContractPreviewModal";
import { fetchBessContractByToken, signBessContractByToken, BessSignTokenContractData } from "../api/bessSigning";
import { formatNumber } from "../utils/formatNumber";

type PageState =
  | "loading"
  | "ready"
  | "signing_success"
  | "error_expired"
  | "error_signed"
  | "error_invalid"
  | "waiting_injection"
  | "waiting_offtake";

function mapToContractData(data: BessSignTokenContractData): BessOnboardingContractData {
  const billingAddress =
    data.billing_info.address && data.billing_info.zip && data.billing_info.city
      ? `${data.billing_info.address}\n${data.billing_info.zip} ${data.billing_info.city}`
      : data.billing_info.address ?? "";

  return {
    startDate: data.start_date,
    actualStart: data.start_date,
    profitShare: `${formatNumber(parseFloat(data.profit_share), "profitShare")}%`,
    monthlyFee: `€ ${formatNumber(parseFloat(data.monthly_fee), "currency")} / month`,
    partner: data.partner_name ?? "—",
    peakPower: `${formatNumber(data.inverter_power / 1000, "power")} kVA`,
    inverterPower: `${formatNumber(data.inverter_power / 1000, "power")} kVA`,
    controller: data.controller,
    opportunityCost: Number.isFinite(Number(data.opportunity_cost))
      ? formatNumber(Number(data.opportunity_cost), "feePerMwh")
      : String(data.opportunity_cost),
    validUntil: data.end_date ?? "",
    company: {
      name: data.company_name,
      address: billingAddress,
      vat: data.billing_info.vat_code,
      email: data.billing_info.email,
      iban: data.billing_info.iban,
    },
    installationId: data.installation_id,
    companyId: data.company_id,
    siteName: data.site_name,
    endDate: data.end_date,
    partnerId: data.partner_id,
    profitShareValue: data.profit_share,
    monthlyFeeValue: data.monthly_fee,
    steeringFeePerformancePct: data.steering_fee_performance_pct,
    inverterPowerKva: data.inverter_power,
    opportunityCostValue: data.opportunity_cost,
    injectionInstallationId: data.injection_installation_id,
    offtakeInstallationId: data.offtake_installation_id,
  };
}

export default function SignBessContractPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation("addservice");
  const [state, setState] = useState<PageState>("loading");
  const [contractData, setContractData] = useState<BessSignTokenContractData | null>(null);

  useEffect(() => {
    if (!token) { setState("error_invalid"); return; }

    fetchBessContractByToken(token)
      .then((data) => {
        setContractData(data);
        setState("ready");
      })
      .catch((err: AxiosError) => {
        if (err.response?.status === 410) {
          const detail = (err.response?.data as { detail?: string })?.detail ?? "";
          setState(detail.toLowerCase().includes("signed") ? "error_signed" : "error_expired");
        } else {
          setState("error_invalid");
        }
      });
  }, [token]);

  const handleSign = async (payload: ContractSigningPayload) => {
    if (!token || !contractData) return;
    try {
      await signBessContractByToken(token, { ...payload, contract_id: contractData.contract_id });
      setState("signing_success");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const reason = (err.response.data as { signBlockedReason?: string })?.signBlockedReason;
        setState(reason === "OFFTAKE_UNSIGNED" ? "waiting_offtake" : "waiting_injection");
        throw new Error("waiting for prerequisite signature");
      }
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 422 &&
        (err.response.data as { error?: string })?.error === "CONTROLLER_REQUIRED"
      ) {
        alert(t("controllerRequired.toast", { ns: "bessOnboarding" }));
        throw new Error("controller required");
      }
      alert(t("signingPage.submitError"));
      throw new Error("signing failed");
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (state === "signing_success") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.successTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.successMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "error_expired") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.expiredTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.expiredMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "error_signed") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.alreadySignedTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.alreadySignedMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "waiting_injection" || state === "waiting_offtake") {
    const titleKey =
      state === "waiting_injection"
        ? "signingPage.waitingInjectionTitle"
        : "signingPage.waitingOfftakeTitle";
    const messageKey =
      state === "waiting_injection"
        ? "signingPage.waitingInjectionMessage"
        : "signingPage.waitingOfftakeMessage";
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">⏳</div>
          <h1 className="text-2xl font-bold mb-2">{t(titleKey)}</h1>
          <p className="text-base-content/70">{t(messageKey)}</p>
        </div>
      </div>
    );
  }

  if (state === "error_invalid" || !contractData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">⚠</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.invalidTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.invalidMessage")}</p>
        </div>
      </div>
    );
  }

  return (
    <ContractPreviewModal
      isOpen={true}
      onClose={() => { /* public signing page — no close action */ }}
      onAccept={handleSign}
      pdfUrl={contractData.pdf_url}
      contractData={mapToContractData(contractData)}
      signBlockedReason={contractData.signBlockedReason ?? null}
    />
  );
}
